import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  Loader2,
  PauseCircle,
  PlayCircle,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Trash2,
  Trophy,
  X,
} from "lucide-react";
import FixtureActionConfirmModal, {
  FIXTURE_CONFIRM,
} from "../components/operator/FixtureActionConfirmModal";
import FixtureDeskIntel from "../components/operator/FixtureDeskIntel";
import FixtureDeskSidebar from "../components/operator/FixtureDeskSidebar";
import FixtureKickoffSoonBanner from "../components/operator/FixtureKickoffSoonBanner";
import OperatorConsoleShell from "../components/operator/OperatorConsoleShell";
import { StatusMeter, TeamLogo } from "../components/bondhouse/FixtureStatus";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  clearHouseFixtures,
  fetchHouseFixtures,
  fetchOperatorDashboard,
  removeHouseFixture,
  suspendHouseFixture,
  unsuspendHouseFixture,
} from "../redux/operatorConsoleSlice";
import { isBetHousePartnershipReadyForConsole } from "../utils/betHousePartners";
import {
  canSuspendFixture,
  FIXTURE_TAB,
  parseFixtureDeskTab,
  bucketFixtures,
  FIXTURE_ONE_X_TWO_CELLS,
  fixtureKickoffShort,
  fixtureMatchLabel,
  fixtureStatusPayload,
  fixtureTabLabel,
  formatFixtureKickoff,
  formatFixtureOneXTwoValue,
  getFixtureOneXTwoFullTime,
  isLiveFixture,
  pickNextKickoffFixture,
  removeDisabledReason,
  resolveFixtureId,
  suspendDisabledReason,
} from "../utils/fixturesDeskHelpers";
import "../betdetail.css";
import "../p2p-create.css";
import "../bettors-desk.css";

const FixtureDeskRow = ({
  item,
  activeTab,
  partnershipReady,
  bet,
  houseFixtureCount,
  busy,
  actionTarget,
  onSuspendRequest,
  onRemoveRequest,
  houseId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const fixtureId = resolveFixtureId(item);
  const detailsId = `fd-fixture-details-${fixtureId}`;
  const isPast = activeTab === FIXTURE_TAB.PAST;
  const isSuspended = !!item.suspended;
  const isLive = isLiveFixture(item);
  const isBusy = busy && actionTarget?.fixtureId === fixtureId;
  const home = item.homeTeam || {};
  const away = item.awayTeam || {};

  const suspendReason = suspendDisabledReason(item, { partnershipReady });
  const removeReason = removeDisabledReason(item, {
    partnershipReady,
    bet,
    houseFixtureCount,
  });
  const suspendDisabled = busy || Boolean(suspendReason && !isSuspended);
  const removeDisabled = busy || Boolean(removeReason);
  const oddsCustomizePath =
    !isPast && houseId && fixtureId
      ? `/app/bethouse/manage/${houseId}/fixtures/${fixtureId}/odds`
      : null;
  const oddsCtaLabel = item.oddsConfigured ? "Update odds" : "Set odds";
  const fullTimeOdds = getFixtureOneXTwoFullTime(item);
  const marketsCount = Number(item.marketsOffered) || 0;
  const bettorsCount = Number(item.bettorsCount) || 0;
  const hasIntel =
    Boolean(item.predictionCounts) ||
    Boolean(item.standings?.home || item.standings?.away);
  const rowTone =
    isLive && !isPast
      ? "live"
      : isSuspended && !isPast
      ? "paused"
      : !item.oddsConfigured && !isPast
      ? "warn"
      : isPast
      ? "past"
      : "idle";

  const metaParts = [];
  if (marketsCount > 0) metaParts.push(`${marketsCount} markets`);
  if (bettorsCount > 0) metaParts.push(`${bettorsCount} bettors`);
  if (hasIntel) metaParts.push("Insights");

  return (
    <li
      className={[
        "fd-fixture-item",
        `fd-fixture-item-${rowTone}`,
        expanded ? "fd-fixture-item-open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="fd-fixture-item-main">
        <div className="fd-fixture-item-head">
          <div className="fd-fixture-item-teams">
            <span className="fd-fixture-team-pair">
              <TeamLogo team={home} size="sm" />
              <span className="fd-fixture-team-name" title={home.name}>
                {home.name || "Home"}
              </span>
            </span>
            <div className="fd-fixture-hub">
              <StatusMeter fixture={fixtureStatusPayload(item)} variant="compact" />
            </div>
            <span className="fd-fixture-team-pair fd-fixture-team-pair-away">
              <span className="fd-fixture-team-name" title={away.name}>
                {away.name || "Away"}
              </span>
              <TeamLogo team={away} size="sm" />
            </span>
          </div>

          <div className="fd-fixture-acts">
            {oddsCustomizePath ? (
              <Link
                to={oddsCustomizePath}
                className={`fd-fixture-act fd-fixture-act-odds ${
                  item.oddsConfigured ? "is-set" : "is-warn"
                }`}
              >
                <SlidersHorizontal size={12} aria-hidden />
                <span>{oddsCtaLabel}</span>
              </Link>
            ) : null}
            {!isPast ? (
              <>
                <button
                  type="button"
                  className={`fd-fixture-act fd-fixture-act-icon ${
                    isSuspended ? "is-resume" : "is-warn"
                  }`}
                  disabled={suspendDisabled}
                  title={
                    suspendReason ||
                    (isSuspended ? "Unsuspend fixture" : "Suspend fixture")
                  }
                  onClick={() => onSuspendRequest(item, fixtureId, isSuspended)}
                >
                  {isBusy && actionTarget?.type === "suspend" ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : isSuspended ? (
                    <PlayCircle size={13} />
                  ) : (
                    <PauseCircle size={13} />
                  )}
                  <span className="fd-action-sr">
                    {isSuspended ? "Resume fixture" : "Pause fixture"}
                  </span>
                </button>
                <button
                  type="button"
                  className="fd-fixture-act fd-fixture-act-icon is-danger"
                  disabled={removeDisabled}
                  title={removeReason || "Remove from house board"}
                  onClick={() => onRemoveRequest(item, fixtureId)}
                >
                  {isBusy && actionTarget?.type === "remove" ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Trash2 size={13} />
                  )}
                  <span className="fd-action-sr">Remove fixture</span>
                </button>
              </>
            ) : null}
            <button
              type="button"
              className={`fd-fixture-act fd-fixture-act-icon fd-fixture-act-more ${
                expanded ? "is-open" : ""
              }`}
              aria-expanded={expanded}
              aria-controls={detailsId}
              aria-label={expanded ? "Hide fixture details" : "Show fixture details"}
              onClick={() => setExpanded((v) => !v)}
            >
              <ChevronDown size={14} aria-hidden />
            </button>
          </div>
        </div>

        <div className="fd-fixture-item-sub">
          <div
            className={[
              "fd-fixture-1x2",
              isPast ? "fd-fixture-1x2-past" : "",
              !item.oddsConfigured ? "fd-fixture-1x2-pending" : "",
              isSuspended && !isPast ? "fd-fixture-1x2-paused" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-label="1X2 full time odds"
          >
            {FIXTURE_ONE_X_TWO_CELLS.map(({ key, label }) => (
              <span key={key} className="fd-fixture-odd">
                <em>{label}</em>
                <strong
                  className={[
                    "tabular-nums",
                    formatFixtureOneXTwoValue(fullTimeOdds?.[key]) === "—"
                      ? "is-empty"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {formatFixtureOneXTwoValue(fullTimeOdds?.[key])}
                </strong>
              </span>
            ))}
          </div>

          <div className="fd-fixture-meta">
            <span className="fd-fixture-meta-league" title={item.league?.name || "League"}>
              <Trophy size={10} aria-hidden />
              {item.league?.name || "League"}
            </span>
            <span className="fd-fixture-meta-sep" aria-hidden>
              ·
            </span>
            <time
              className="fd-fixture-meta-time tabular-nums"
              dateTime={item.date}
              title={formatFixtureKickoff(item)}
            >
              {fixtureKickoffShort(item)}
            </time>
            {isSuspended && !isPast ? (
              <span className="fd-fixture-tag fd-fixture-tag-paused" role="status">
                Paused
              </span>
            ) : null}
            {!item.oddsConfigured && !isPast ? (
              <span className="fd-fixture-tag fd-fixture-tag-warn">Needs odds</span>
            ) : null}
            {metaParts.length > 0 ? (
              <>
                <span className="fd-fixture-meta-sep" aria-hidden>
                  ·
                </span>
                <span className="fd-fixture-meta-stats">{metaParts.join(" · ")}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div
        id={detailsId}
        className={`fd-fixture-item-details ${expanded ? "is-open" : ""}`}
        aria-hidden={!expanded}
      >
        <div className="fd-fixture-item-details-inner">
          <FixtureDeskIntel
            item={item}
            fixtureId={fixtureId}
            houseId={houseId}
            isPast={isPast}
            hideOddsAction
          />
        </div>
      </div>
    </li>
  );
};

const FixturesDesk = () => {
  const { houseId } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const userId = useSelector(selectAppUser)?._id;

  const {
    houses,
    houseFixtures,
    houseFixturesStatus,
    houseFixturesError,
    houseFixturesActionLoading,
  } = useSelector((s) => s.operatorConsole);

  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(
    () => parseFixtureDeskTab(searchParams.get("tab")) ?? FIXTURE_TAB.UPCOMING
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [suspendedOnly, setSuspendedOnly] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const tab = parseFixtureDeskTab(searchParams.get("tab"));
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const bet = useMemo(
    () => houses.find((h) => String(h._id) === String(houseId)),
    [houses, houseId]
  );
  const partnershipReady = isBetHousePartnershipReadyForConsole(bet);
  const addFixturesPath = `/app/bethouse/manage/${houseId}/fixtures/add`;

  const reload = useCallback(() => {
    if (!houseId) return Promise.resolve();
    return dispatch(fetchHouseFixtures({ betId: houseId }));
  }, [dispatch, houseId]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    return () => dispatch(clearHouseFixtures());
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        reload(),
        dispatch(fetchOperatorDashboard(houseId)).unwrap(),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const buckets = useMemo(() => bucketFixtures(houseFixtures), [houseFixtures]);
  const nextKickoffFixture = useMemo(
    () => pickNextKickoffFixture(houseFixtures),
    [houseFixtures]
  );
  const tabCounts = useMemo(
    () => ({
      [FIXTURE_TAB.NEED_ODDS]: buckets[FIXTURE_TAB.NEED_ODDS].length,
      [FIXTURE_TAB.UPCOMING]: buckets[FIXTURE_TAB.UPCOMING].length,
      [FIXTURE_TAB.LIVE]: buckets[FIXTURE_TAB.LIVE].length,
      [FIXTURE_TAB.PAST]: buckets[FIXTURE_TAB.PAST].length,
    }),
    [buckets]
  );

  const baseRows = buckets[activeTab] || [];
  const filteredRows = useMemo(() => {
    let rows = [...baseRows];
    if (suspendedOnly) rows = rows.filter((r) => r.suspended);
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const label = fixtureMatchLabel(r).toLowerCase();
        const league = (r.league?.name || "").toLowerCase();
        return label.includes(q) || league.includes(q);
      });
    }
    return rows;
  }, [baseRows, searchQuery, suspendedOnly]);

  const runFixtureAction = async (type, fixtureId, action, successMessage) => {
    setActionTarget({ fixtureId, type });
    try {
      await dispatch(action).unwrap();
      await reload();
      dispatch(fetchOperatorDashboard(houseId));
      if (successMessage) {
        setToast({ type: "success", message: successMessage });
      }
      return true;
    } catch (err) {
      setToast({
        type: "error",
        message: typeof err === "string" ? err : "Action failed. Try again.",
      });
      return false;
    } finally {
      setActionTarget(null);
    }
  };

  const handleSuspendRequest = (item, fixtureId, isSuspended) => {
    const reason = suspendDisabledReason(item, { partnershipReady });
    if (!isSuspended && reason) {
      setToast({ type: "error", message: reason });
      return;
    }
    if (!isSuspended && !canSuspendFixture(item)) {
      setToast({
        type: "error",
        message: "Cannot suspend after kickoff or once the match is live.",
      });
      return;
    }
    setConfirmTarget({
      kind: isSuspended ? FIXTURE_CONFIRM.UNSUSPEND : FIXTURE_CONFIRM.SUSPEND,
      item,
      fixtureId,
    });
  };

  const handleRemoveRequest = (item, fixtureId) => {
    const reason = removeDisabledReason(item, {
      partnershipReady,
      bet,
      houseFixtureCount: houseFixtures.length,
    });
    if (reason) {
      setToast({ type: "error", message: reason });
      return;
    }
    setConfirmTarget({
      kind: FIXTURE_CONFIRM.REMOVE,
      item,
      fixtureId,
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmTarget || !userId || !houseId) return;
    const { kind, item, fixtureId } = confirmTarget;

    if (kind === FIXTURE_CONFIRM.SUSPEND) {
      const ok = await runFixtureAction(
        "suspend",
        fixtureId,
        suspendHouseFixture({ betId: houseId, fixtureId, operatorUserId: userId }),
        "Fixture suspended"
      );
      if (ok) setConfirmTarget(null);
      return;
    }

    if (kind === FIXTURE_CONFIRM.UNSUSPEND) {
      const ok = await runFixtureAction(
        "suspend",
        fixtureId,
        unsuspendHouseFixture({ betId: houseId, fixtureId, operatorUserId: userId }),
        "Fixture unsuspended"
      );
      if (ok) setConfirmTarget(null);
      return;
    }

    if (kind === FIXTURE_CONFIRM.REMOVE) {
      const ok = await runFixtureAction(
        "remove",
        fixtureId,
        removeHouseFixture({ betId: houseId, fixtureId, operatorUserId: userId }),
        `${fixtureMatchLabel(item)} removed`
      );
      if (ok) setConfirmTarget(null);
    }
  };

  const suspendedCount = houseFixtures.filter((f) => f.suspended).length;
  const headerSubtitle = bet?.name || "Betting house";
  const headerLaneLine = useMemo(() => {
    const parts = [`${houseFixtures.length} on board`];
    if (tabCounts[FIXTURE_TAB.LIVE] > 0) {
      parts.push(`${tabCounts[FIXTURE_TAB.LIVE]} live`);
    }
    if (tabCounts[FIXTURE_TAB.NEED_ODDS] > 0) {
      parts.push(`${tabCounts[FIXTURE_TAB.NEED_ODDS]} need odds`);
    }
    if (suspendedCount > 0) {
      parts.push(`${suspendedCount} paused`);
    }
    return parts.join(" · ");
  }, [houseFixtures.length, suspendedCount, tabCounts]);

  if (!houseId) {
    return (
      <OperatorConsoleShell activeSection="fixtures" mainClassName="oc-desk-main">
        <div className="bd-page fd-page fd-board-desk">
          <div className="bd-loading">Missing house.</div>
        </div>
      </OperatorConsoleShell>
    );
  }

  return (
    <OperatorConsoleShell activeSection="fixtures" mainClassName="oc-desk-main">
      <div className="bd-page fd-page fd-board-desk">
      <header className="bd-header fd-board-header">
        <div className="bd-header-copy">
          <p className="bd-eyebrow">
            <Calendar size={9} />
            Board desk
          </p>
          <span className="bd-header-title">
            {headerSubtitle}
            {houseFixturesStatus === "succeeded" ? ` · ${headerLaneLine}` : ""}
          </span>
        </div>
        <div className="fd-board-header-actions">
          <div className="fd-desk-search-slot">
            {searchOpen ? (
              <div className="fd-desk-search-popover">
                <div className="fd-desk-search">
                  <Search size={13} aria-hidden />
                  <input
                    ref={searchInputRef}
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search fixtures…"
                    aria-label="Search fixtures"
                  />
                  {searchQuery ? (
                    <button
                      type="button"
                      className="fd-desk-search-clear"
                      aria-label="Clear search"
                      onClick={() => setSearchQuery("")}
                    >
                      <X size={13} />
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
            <button
              type="button"
              className={[
                "bd-icon-btn",
                "fd-desk-search-toggle",
                searchOpen ? "fd-desk-search-toggle-open" : "",
                !searchOpen && searchQuery ? "fd-desk-search-toggle-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setSearchOpen((open) => !open)}
              aria-label={searchOpen ? "Close search" : "Search fixtures"}
              aria-expanded={searchOpen}
            >
              {searchOpen ? (
                <X size={13} strokeWidth={2.3} />
              ) : (
                <Search size={13} strokeWidth={2.3} />
              )}
            </button>
          </div>
          <Link
            to={addFixturesPath}
            className={`bd-icon-btn ${partnershipReady ? "" : "fd-board-add-disabled"}`}
            aria-disabled={!partnershipReady}
            title={
              partnershipReady
                ? "Add fixtures"
                : "All partners must accept before adding fixtures"
            }
            onClick={(e) => {
              if (!partnershipReady) e.preventDefault();
            }}
          >
            <Plus size={13} />
          </Link>
          <button
            type="button"
            className="bd-icon-btn"
            onClick={handleRefresh}
            disabled={refreshing || houseFixturesStatus === "loading"}
            aria-label="Refresh fixtures"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      <div className="bd-desk-body">
      {toast ? (
        <div
          className={`bd-toast bd-toast-${
            toast.type === "error" ? "error" : toast.type === "info" ? "info" : "success"
          }`}
          role="status"
        >
          {toast.message}
        </div>
      ) : null}

      {!partnershipReady ? (
        <div className="fd-alert" role="status">
          <AlertCircle size={15} aria-hidden />
          <p>All partners must accept before you can add, suspend, or remove fixtures.</p>
        </div>
      ) : null}

      <div className="bd-bettors-command fd-board-command">
        <aside className="bd-roster-rail fd-board-rail">
          <section className="bd-roster-rail-scroll fd-board-rail-scroll">
            <header className="bd-rail-head">
              <h2>Lanes</h2>
              <span className="tabular-nums">{filteredRows.length}</span>
            </header>
            <FixtureDeskSidebar
              layout="rail"
              activeTab={activeTab}
              onTabChange={setActiveTab}
              tabCounts={tabCounts}
              suspendedCount={suspendedCount}
              suspendedOnly={suspendedOnly}
              onSuspendedOnlyChange={setSuspendedOnly}
            />
          </section>
        </aside>

        <div className="fd-board-workspace">
          <FixtureKickoffSoonBanner fixture={nextKickoffFixture} />

          <section className="fd-board">
          {houseFixturesError ? (
            <div className="fd-alert fd-alert-error" role="alert">
              <AlertCircle size={15} aria-hidden />
              <p>{String(houseFixturesError)}</p>
              <button type="button" onClick={reload}>
                Retry
              </button>
            </div>
          ) : null}

          {houseFixturesStatus === "loading" && !houseFixtures.length ? (
            <div className="bd-loading bd-loading-compact fd-board-loading">
              <Loader2 size={18} className="animate-spin" />
              <span>Loading fixtures…</span>
            </div>
          ) : null}

          {!filteredRows.length && houseFixturesStatus === "succeeded" ? (
            <div className="bd-empty-state fd-empty">
              <strong>No fixtures in {fixtureTabLabel(activeTab).toLowerCase()}</strong>
              <p>
                {houseFixtures.length
                  ? "Try another lane or clear your filters."
                  : "Browse the catalog to add games to your market board."}
              </p>
              {!houseFixtures.length && partnershipReady ? (
                <Link to={addFixturesPath} className="fd-add-btn fd-add-btn-link">
                  <Plus size={14} />
                  Add fixtures
                </Link>
              ) : null}
            </div>
          ) : null}

          <ul className="fd-fixture-list">
            {filteredRows.map((item) => (
              <FixtureDeskRow
                key={resolveFixtureId(item)}
                item={item}
                activeTab={activeTab}
                partnershipReady={partnershipReady}
                bet={bet}
                houseFixtureCount={houseFixtures.length}
                busy={houseFixturesActionLoading}
                actionTarget={actionTarget}
                onSuspendRequest={handleSuspendRequest}
                onRemoveRequest={handleRemoveRequest}
                houseId={houseId}
              />
            ))}
          </ul>
          </section>
        </div>
      </div>

      <FixtureActionConfirmModal
        open={Boolean(confirmTarget)}
        kind={confirmTarget?.kind}
        item={confirmTarget?.item}
        busy={houseFixturesActionLoading}
        onClose={() => !houseFixturesActionLoading && setConfirmTarget(null)}
        onConfirm={handleConfirmAction}
      />
      </div>
      </div>
    </OperatorConsoleShell>
  );
};

export default FixturesDesk;
